import {
  BasisTheoryElements,
  ICardExpirationDateElement,
  ICardNumberElement,
  ICardVerificationCodeElement,
  Token,
  TokenIntentCardDetails,
  useBasisTheory,
} from '@basis-theory/react-elements'

type BasisTheoryOperationType = 'token-intent' | 'token-update'

export type BasisTheoryClientErrorType = 'missing-key' | 'init-error' | 'not-ready'

export class BasisTheoryClientError extends Error {
  readonly type: BasisTheoryClientErrorType

  constructor(type: BasisTheoryClientErrorType, message: string) {
    super(message)
    this.type = type
  }
}

interface CreateCardTokenIntentParams {
  numberEl: ICardNumberElement
  expEl: ICardExpirationDateElement
  cvcEl: ICardVerificationCodeElement
}

type BTCardDetails = TokenIntentCardDetails['card']

export interface CardDetail extends BTCardDetails {
  authentication?: string
}

interface UpdateTokenCvcParams {
  tokenId: string
  cvcEl: ICardVerificationCodeElement
}

const getBasisTheoryEnvironment = (): 'test' | 'production' => {
  const appEnv = import.meta.env.VITE_ENV || import.meta.env.MODE
  return appEnv === 'prod' || appEnv === 'production' ? 'production' : 'test'
}

const getBasisTheoryPublicKey = (): string | undefined => {
  const singleKey = import.meta.env.VITE_BASIS_THEORY_PUBLIC_KEY
  return singleKey || undefined
}

const assertBtReady = (
  bt: BasisTheoryElements | undefined,
  operation: BasisTheoryOperationType,
  key?: string,
  initError?: Error
): BasisTheoryElements => {
  if (!key) {
    throw new BasisTheoryClientError(
      'missing-key',
      `Basis Theory ${operation.replace('-', ' ')} public key is not configured.`
    )
  }

  if (initError) {
    throw new BasisTheoryClientError(
      'init-error',
      `Basis Theory ${operation.replace('-', ' ')} client failed to initialize.`
    )
  }

  if (!bt) {
    throw new BasisTheoryClientError(
      'not-ready',
      `Basis Theory ${operation.replace('-', ' ')} client is not ready.`
    )
  }

  return bt
}

export const useBasisTheoryClients = () => {
  const environment = getBasisTheoryEnvironment()
  const publicKey = getBasisTheoryPublicKey()

  const btClient = useBasisTheory(publicKey, { environment })

  const createCardTokenIntent = async ({
    numberEl,
    expEl,
    cvcEl,
  }: CreateCardTokenIntentParams): Promise<{ id: string; authentication?: string }> => {
    const bt = assertBtReady(btClient.bt, 'token-intent', publicKey, btClient.error)

    const intent = await bt.tokenIntents.create({
      type: 'card',
      data: {
        number: numberEl,
        expiration_month: expEl.month(),
        expiration_year: expEl.year(),
        cvc: cvcEl,
      },
    })
    return {
      id: intent.id,
      authentication: 'card' in intent ? (intent.card as CardDetail).authentication : '',
    }
  }

  const updateTokenCvc = async ({ tokenId, cvcEl }: UpdateTokenCvcParams): Promise<Token> => {
    const bt = assertBtReady(btClient.bt, 'token-update', publicKey, btClient.error)

    return await bt.tokens.update(tokenId, { data: { cvc: cvcEl } })
  }

  return {
    btClient,
    createCardTokenIntent,
    updateTokenCvc,
  }
}
